// Typed import-batch endpoint functions. Backed by GET/POST /api/v1/imports/*.
//
// Per CHG-1260 plan — consumed by @generated/screens/import-batch-list.ts +
// import-batch-detail.ts queryFn callbacks AND by the import-map wizard
// (CHG-1247) which posts to createImportBatch + executeImportBatch.
//
// T2 entity port is forward-promised per specs/tier-05-experience/
// import-batch.entity-hint.yaml (CHG-1235).

import { apiClient } from "./client";
import type {
  ImportBatch,
  ImportBatchPagedEnvelope,
  ImportRow,
  ImportMapping,
  ImportEntityType,
} from "./types";

export interface ListImportBatchesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  entity_type?: ImportEntityType;
}

export async function listImportBatches(
  params: ListImportBatchesParams = {},
): Promise<{ items: ImportBatch[]; total: number }> {
  const r = await apiClient().get<ImportBatchPagedEnvelope>("/imports", {
    params: {
      offset: (params.page ?? 0) * (params.pageSize ?? 50),
      limit: params.pageSize ?? 50,
      sort: params.sortField ? `${params.sortField}:${params.sortDirection ?? "asc"}` : undefined,
      q: params.search,
      status: params.status,
      entity_type: params.entity_type,
    },
  });
  return { items: r.data.items, total: r.data.total };
}

export async function getImportBatch(id: string): Promise<ImportBatch> {
  const r = await apiClient().get<ImportBatch>(`/imports/${id}`);
  return r.data;
}

export interface CreateImportBatchBody {
  source_file: unknown;
  entity_type: ImportEntityType;
  default_campaign_id?: string;
}

/** POST /imports — phase 1 of the import-map wizard. Creates the batch + stages rows. */
export async function createImportBatch(body: CreateImportBatchBody): Promise<ImportBatch> {
  const r = await apiClient().post<ImportBatch>("/imports", body);
  return r.data;
}

export interface ExecuteImportBatchBody {
  column_mapping: Record<string, string | null>;
  run_at?: string | null;
  notify_email?: string | null;
}

/** POST /imports/{id}/execute — phase 2 of the import-map wizard. Maps + runs. */
export async function executeImportBatch(
  id: string,
  body: ExecuteImportBatchBody,
): Promise<ImportBatch> {
  const r = await apiClient().post<ImportBatch>(`/imports/${id}/execute`, body);
  return r.data;
}

/** GET /imports/{id}/rows — used by ImportPreviewTable to show the first N rows. */
export async function getImportRows(
  id: string,
  params: { limit?: number; offset?: number } = {},
): Promise<{ items: ImportRow[]; total: number }> {
  const r = await apiClient().get<{ items: ImportRow[]; total: number }>(`/imports/${id}/rows`, {
    params: { limit: params.limit ?? 10, offset: params.offset ?? 0 },
  });
  return r.data;
}

/** GET /imports/{id}/mapping — used by ImportColumnMappingTable to read existing mapping + auto-detected source columns. */
export async function getImportMapping(id: string): Promise<ImportMapping> {
  const r = await apiClient().get<ImportMapping>(`/imports/${id}/mapping`);
  return r.data;
}

export async function cancelImportBatch(id: string): Promise<ImportBatch> {
  const r = await apiClient().post<ImportBatch>(`/imports/${id}/cancel`, {});
  return r.data;
}

export const importBatchesApi = {
  listImportBatches,
  getImportBatch,
  createImportBatch,
  executeImportBatch,
  getImportRows,
  getImportMapping,
  cancelImportBatch,
};
